import AccountPicker from "../../components/AccountPicker";
import { useState } from "react";
import type { Account, Owner, SavingGoal, Transaction } from "../../types";
import { api } from "../../lib/api";
import { resolveGoalCushionAccountId } from "../../lib/goal-reserves";
import { goalFundingAvailability, goalTopUpLimit, isGoalFundingAccount } from "../../lib/goal-funding";
import GoalFundingFields from "./GoalFundingFields";
import FormActions from "../../components/FormActions";
import Modal from "../../components/Modal";

export interface GoalTopUpModalProps {
  goal: SavingGoal;
  goals: SavingGoal[];
  transactions: Transaction[];
  accounts: Account[];
  profiles: Owner[];
  today: string;
  month: string;
  initialMode?: "topup" | "skip" | "resume";
  onClose: () => void;
  onSaved: () => void;
}

export default function GoalTopUpModal({
  goal,
  goals,
  transactions,
  accounts,
  profiles,
  today,
  month,
  initialMode = "topup",
  onClose,
  onSaved,
}: GoalTopUpModalProps) {
  const linkedAccountId = goal.schedules.length === 0 ? goal.cushionAccountId : null;
  const activeAccounts = accounts.filter(a => isGoalFundingAccount(a, goal.currency) && (!linkedAccountId || a.id === linkedAccountId));
  const [mode, setMode] = useState<"topup" | "skip" | "resume">(initialMode);
  const [amount, setAmount] = useState(mode === "skip" ? "0" : "");
  const [accountId, setAccountId] = useState(
    () =>
      activeAccounts.find(a => a.id === resolveGoalCushionAccountId(goal, accounts))?.id ??
      activeAccounts[0]?.id ??
      "",
  );
  const [profile, setProfile] = useState<string>(goal.profile ?? profiles[0] ?? "");
  const [note, setNote] = useState(mode === "skip" ? "Skipped this month" : "");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const account = activeAccounts.find(a => a.id === accountId);
  const allocation = account ? goalFundingAvailability(account, goals, accounts, today.slice(0, 7), transactions) : undefined;
  const limit = goalTopUpLimit(goal, allocation?.safeToSpend ?? 0, today.slice(0, 7));
  const validAmount = Number.isFinite(Number(amount)) && Number(amount) > 0 && Number(amount) <= limit.maximumAmount;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving || (mode === "topup" && !validAmount)) return;
    setSaving(true);
    setError("");

    try {
      if (mode === "skip") {
        await api("v2/goals/contribute", {
          requestId,
          goalId: goal.id,
          profile: profile || null,
          month,
          date: today,
          amount: "0",
          kind: "skip",
          note: note.trim() || "Skipped this month",
        });
      } else if (mode === "resume") {
        await api("v2/goals/contribute", {
          requestId,
          goalId: goal.id,
          profile: profile || null,
          month,
          date: today,
          amount: "0",
          kind: "resume",
          note: note.trim(),
        });
      } else {
        await api("v2/goals/contribute", {
          requestId,
          goalId: goal.id,
          accountId: accountId || null,
          profile: profile || null,
          month: null,
          date: today,
          amount,
          kind: "ad_hoc",
          note: note.trim(),
        });
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record contribution.");
      setSaving(false);
    }
  }

  return (
    <Modal
      title={
        mode === "skip"
          ? `Skip ${month} for ${goal.name}`
          : mode === "resume"
          ? `Resume ${month} for ${goal.name}`
          : `Add funds to ${goal.name}`
      }
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="goal-form">
        {error && <div className="form-error" role="alert">{error}</div>}

        <div className="entry-tabs" role="tablist" style={{ marginBottom: "0.5rem" }}>
          <button
            type="button"
            role="tab"
            aria-pressed={mode === "topup"}
            onClick={() => { setMode("topup"); setAmount(""); setRequestId(crypto.randomUUID()); }}
          >
            Ad-hoc top up
          </button>
          <button
            type="button"
            role="tab"
            aria-pressed={mode === "skip"}
            onClick={() => { setMode("skip"); setAmount("0"); setRequestId(crypto.randomUUID()); }}
          >
            Skip {month}
          </button>
          <button
            type="button"
            role="tab"
            aria-pressed={mode === "resume"}
            onClick={() => { setMode("resume"); setAmount("0"); setRequestId(crypto.randomUUID()); }}
          >
            Resume {month}
          </button>
        </div>

        {mode === "skip" ? (
          <p style={{ fontSize: "0.85rem", color: "var(--muted)", margin: "0.25rem 0" }}>
            Skipping <strong>{month}</strong> pauses planned contributions for this month. That cash stays in your account’s Safe-to-Spend balance, and your estimated completion date shifts forward.
          </p>
        ) : mode === "resume" ? (
          <p style={{ fontSize: "0.85rem", color: "var(--muted)", margin: "0.25rem 0" }}>
            Resuming <strong>{month}</strong> restores your scheduled monthly commitments and returns them to auto-accrual.
          </p>
        ) : (
          <>
            <AccountPicker label="Reserve in account" accounts={activeAccounts} value={accountId} onChange={(id) => { setAccountId(id); setAmount(""); setRequestId(crypto.randomUUID()); }} />

            <GoalFundingFields account={account} currency={goal.currency} allocation={allocation} limit={limit}
              amount={amount} onAmountChange={value => { setAmount(value); setRequestId(crypto.randomUUID()); }} />

            <label>
              <span>Profile</span>
              <select
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
              >
                <option value="">Household</option>
                {profiles.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
          </>
        )}

        <label>
          <span>Note (optional)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === "skip" ? "e.g. Tight month, car repair" : "e.g. Birthday gift, extra savings"}
          />
        </label>

        <FormActions
          label={mode === "skip" ? "Confirm skip" : mode === "resume" ? "Resume accrual" : "Add funds"}
          busy={saving}
          disabled={mode !== "skip" && mode !== "resume" && !validAmount}
        />
      </form>
    </Modal>
  );
}
